import { jsPDF } from "jspdf";
import QRCode from "qrcode";

const NAVY = "#0d1724";
const GOLD = "#d6aa43";
const CREAM = "#f8f4e8";

async function makeQrDataUrl(text) {
  return QRCode.toDataURL(text, {
    errorCorrectionLevel: "M",
    margin: 1,
    width: 320,
    color: { dark: "#0d1724", light: "#ffffff" },
  });
}

/**
 * Generate a branded PDF receipt for an application submission.
 * @param {object} opts
 * @param {string} opts.serial - DKF-<code>-YYYY-XXXXXX
 * @param {string} opts.applicantName
 * @param {string} opts.category - primary_category key
 * @param {string} opts.dateISO
 * @param {string} opts.trackUrl - full URL for tracking (encoded in QR)
 * @param {string} [opts.categoryLabel]
 */
export async function generateApplicationReceipt(opts) {
  const { serial, applicantName, category, dateISO, trackUrl, categoryLabel } = opts;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Navy header block
  doc.setFillColor(NAVY);
  doc.rect(0, 0, pageW, 120, "F");

  // Gold band
  doc.setFillColor(GOLD);
  doc.rect(0, 120, pageW, 4, "F");

  // Brand text
  doc.setTextColor(GOLD);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("DUTSEN KAUNA FOUNDATION", 40, 45);
  doc.setTextColor("#ffffff");
  doc.setFontSize(20);
  doc.setFont("times", "bold");
  doc.text("Application receipt", 40, 78);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor("#aeb8c6");
  doc.text("Innovation • Impact • Integrity • Love", 40, 97);

  // Body
  let y = 160;
  doc.setTextColor(NAVY);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Applicant", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(14);
  doc.text(applicantName || "—", 40, y + 22);
  y += 60;

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Application category", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text(categoryLabel || category || "—", 40, y + 20);
  y += 55;

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Date submitted", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text(new Date(dateISO || Date.now()).toLocaleString(), 40, y + 20);
  y += 55;

  // Serial in a gold-outlined navy card
  const cardY = y;
  doc.setDrawColor(GOLD);
  doc.setLineWidth(2);
  doc.setFillColor(NAVY);
  doc.roundedRect(40, cardY, pageW - 80, 90, 8, 8, "FD");
  doc.setTextColor(GOLD);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("APPLICATION SERIAL NUMBER", 60, cardY + 26);
  doc.setFont("courier", "bold");
  doc.setFontSize(22);
  doc.text(serial, 60, cardY + 62);
  y = cardY + 120;

  // QR
  try {
    const qrUrl = await makeQrDataUrl(trackUrl);
    doc.addImage(qrUrl, "PNG", pageW - 180, y, 140, 140);
  } catch (e) { console.warn("QR code omitted from receipt", e); }
  doc.setTextColor(NAVY);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Track your application", 40, y + 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Scan the code, or visit:", 40, y + 40);
  doc.setTextColor("#0057d9");
  const shortUrl = trackUrl.length > 60 ? trackUrl.slice(0, 57) + "..." : trackUrl;
  doc.text(shortUrl, 40, y + 58);
  doc.setTextColor(NAVY);
  doc.setFontSize(9);
  const notes = [
    "Applying for help is free — the Foundation never charges applicants.",
    "Please keep this receipt safe. It is your official reference for verification,",
    "assessment and communication with Dutsen Kauna Foundation.",
    "",
    "Submitting an application does not automatically guarantee approval or support.",
  ];
  notes.forEach((line, i) => doc.text(line, 40, y + 90 + i * 14));

  // Footer strip
  doc.setFillColor(CREAM);
  doc.rect(0, pageH - 60, pageW, 60, "F");
  doc.setTextColor(NAVY);
  doc.setFontSize(9);
  doc.text("Dutsen Kauna Foundation", 40, pageH - 32);
  doc.setTextColor("#666666");
  doc.setFontSize(8);
  doc.text("foundation@dutsenkauna.org  •  +234 806 355 0963", 40, pageH - 18);

  doc.save(`${serial}-receipt.pdf`);
}

export async function generateDonationReceipt({ serial, name, amount, type, method, dateISO, status, impact }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFillColor(NAVY); doc.rect(0, 0, pageW, 120, "F");
  doc.setFillColor(GOLD); doc.rect(0, 120, pageW, 4, "F");
  doc.setTextColor(GOLD); doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.text("DUTSEN-KAUNA FOUNDATION", 40, 45);
  doc.setTextColor("#ffffff"); doc.setFontSize(20); doc.setFont("times", "bold"); doc.text("Donation receipt", 40, 78);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#aeb8c6"); doc.text("Innovation • Impact • Integrity • Love", 40, 97);
  let y = 160; doc.setTextColor(NAVY);
  const row = (label, value) => { doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.text(label, 40, y); doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.text(String(value || "—"), 40, y + 20); y += 52; };
  row("Donor", name); row("Amount", amount); row("Gift type", type); row("Payment method", method); row("Status", status);
  row("Date", dateISO ? new Date(dateISO).toLocaleString() : "—");
  doc.setFillColor("#f8f4e8"); doc.roundedRect(40, y, pageW - 80, 70, 8, 8, "F");
  doc.setTextColor(GOLD); doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.text("DONOR REFERENCE", 56, y + 22);
  doc.setTextColor(NAVY); doc.setFontSize(18); doc.setFont("courier", "bold"); doc.text(serial, 56, y + 50); y += 100;
  doc.setFont("helvetica", "italic"); doc.setFontSize(11); doc.setTextColor("#333333"); doc.text(doc.splitTextToSize(impact || "", pageW - 80), 40, y); y += 40;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#555555");
  doc.text(doc.splitTextToSize("Dutsen Kauna Foundation is a registered non-profit. Giving never affects anyone's eligibility for support. Keep this reference for correspondence and verification at /verify.", pageW - 80), 40, y);
  try { const qr = await QRCode.toDataURL(`${window.location.origin}/thank-you/${serial}`, { margin: 1, width: 90 }); doc.addImage(qr, "PNG", pageW - 130, 150, 90, 90); } catch (e) { console.warn("QR code omitted from receipt", e); }
  doc.setFontSize(8); doc.setTextColor("#888888"); doc.text("foundation@dutsenkauna.org  •  +234 806 355 0963", 40, pageH - 18);
  doc.save(`DKF-donation-receipt-${serial}.pdf`);
}


export async function generateVaultReceipt(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(13, 23, 36); doc.rect(0, 0, W, 90, "F");
  doc.setTextColor(214, 170, 67); doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.text("Dutsen Kauna Foundation", 40, 45);
  doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.text("Donation receipt", 40, 66);
  doc.setTextColor(10, 13, 18); doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.text(`Receipt no. ${r.receipt_no}`, 40, 125);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  const rows = [["Date", new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })], ["Donor", r.donor_name], ["Amount", `${r.currency} ${Number(r.amount).toLocaleString()}`],
    ["Purpose / fund", r.purpose + (r.project_serial ? ` (${r.project_serial})` : "")], ["Frequency", (r.donation_type || "one-time").replace(/_/g, " ")], ["Payment channel", r.channel], ["Transaction reference", r.transaction_ref || "—"], ["Status", r.status], ["Donor reference", r.serial]];
  let y = 150;
  rows.forEach(([k, v]) => { doc.setFont("helvetica", "bold"); doc.text(`${k}:`, 40, y); doc.setFont("helvetica", "normal"); doc.text(String(v || "—"), 190, y, { maxWidth: W - 230 }); y += 20; });
  y += 10; doc.setDrawColor(214, 170, 67); doc.line(40, y, W - 40, y); y += 22;
  doc.setFont("helvetica", "bold"); doc.text(r.foundation.name, 40, y); y += 15; doc.setFont("helvetica", "normal");
  [r.foundation.address, `${r.foundation.email}  ·  ${r.foundation.phone}`, r.foundation.registration, "Thank you. Restricted gifts are used only for the named project; unrestricted gifts follow our published target allocation policy."].forEach((l) => { doc.text(l, 40, y, { maxWidth: W - 80 }); y += 15; });
  const qr = await QRCode.toDataURL(`${window.location.origin}/verify?q=${encodeURIComponent(r.serial)}`, { margin: 1, width: 90 });
  doc.addImage(qr, "PNG", W - 130, 110, 90, 90); doc.setFontSize(8); doc.text("Verify this reference", W - 130, 212);
  doc.save(`${r.receipt_no}.pdf`);
}
